import React, { useMemo, useState, useEffect } from 'react';
import {
  Box, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import CommonModal from 'components/common/CommonModal';
import { useAuth } from 'contexts/AuthContext';
import { useClasses } from 'hooks/useClasses';
import { useSessions, SessionData } from 'hooks/useSession';
import { useStudentClassMapping } from 'hooks/useStudentClassMapping';
import { useMarkSheets } from 'hooks/useMarkSheets';
import { useStudents } from 'hooks/useStudents';
import { useTranslation } from 'react-i18next';
import { useError } from 'contexts/ErrorContext';
import { getClassRank } from 'components/enum/ClassOptions';

interface PramoteStudentFormModalProps {
  open: boolean;
  handleClose: () => void;
  studentId?: number;
  studentIds?: number[]; // For bulk promotion
  marksheetType: string;
  monthType?: string;
  studentRollNo?: string;
  currentClassName: string;
  currentSessionName: string;
  showPromoteWithSubjects?: boolean;
  onSuccess?: () => void;
  isBulk?: boolean;
}


const PramoteStudentFormModal: React.FC<PramoteStudentFormModalProps> = ({
  open,
  handleClose,
  studentId,
  studentIds,
  marksheetType,
  monthType,
  studentRollNo,
  currentClassName,
  currentSessionName,
  showPromoteWithSubjects = false,
  onSuccess,
  isBulk = false
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showSuccess, showError } = useError();
  const schoolId = user?.schoolId;
  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { sessionsQuery } = useSessions(schoolId ?? undefined);
  const { promoteStudentMutation } = useStudentClassMapping();
  const { promoteBulkStudentMutation } = useStudents();
  const { useMarksheetByTypeClassRoll } = useMarkSheets();

  const [selectedClassId, setSelectedClassId] = useState<number | ''>('');
  const [selectedSessionId, setSelectedSessionId] = useState<number | ''>('');
  const [schoolType, setSchoolType] = useState('SAME');

  const { data: marksheetData, isLoading: isMonthTypeLoading } =
    useMarksheetByTypeClassRoll(
      marksheetType,
      currentClassName,
      studentRollNo || "",
      open && !!marksheetType && !!currentClassName && !!studentRollNo && !isBulk
    );

  const resolvedMonthType = useMemo(
    () => marksheetData?.monthType || monthType || "JANUARY",
    [marksheetData?.monthType, monthType]
  );

  const [isBulkPromoting, setIsBulkPromoting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClassId || !selectedSessionId) return;

    if (isBulk && studentIds && studentIds.length > 0) {
      // Bulk promotion: promote all selected students sequentially
      setIsBulkPromoting(true);

      try {
        await promoteBulkStudentMutation.mutateAsync({
          studentIds,
          newClassId: selectedClassId,
          sessionId: selectedSessionId,
          marksheetType,
          schoolType,
          withSubjects: showPromoteWithSubjects,
        });

        setIsBulkPromoting(false);
        showSuccess(
          t('students_promoted_successfully', { count: studentIds.length }) ||
          `${studentIds.length} ${studentIds.length === 1 ? 'student' : 'students'} promoted successfully`
        );
        if (onSuccess) onSuccess();
        handleClose();
      } catch (error: any) {
        setIsBulkPromoting(false);
        const errorMessage = error?.response?.data?.message || error?.message || t('error_promoting_students') || 'Failed to promote students. Please try again.';
        showError(errorMessage);
      }
    } else if (studentId) {
      // Single student promotion
      promoteStudentMutation.mutate(
        {
          studentId,
          newClassId: selectedClassId,
          sessionId: selectedSessionId,
          marksheetType,
          schoolType,
          withSubjects: showPromoteWithSubjects,
        },
        {
          onSuccess: () => {
            showSuccess(t('student_promoted_successfully') || 'Student promoted successfully');
            if (onSuccess) onSuccess();
            handleClose();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || t('error_promoting_student') || 'Failed to promote student. Please try again.';
            showError(errorMessage);
          }
        }
      );
    }
  };

  // Filter classes to only those with class rank exactly same or one greater than currentClassName
  const currentClassRank = getClassRank(currentClassName);
  const nextClassRank = currentClassRank + 1;
  const eligibleClasses =
    classesQuery.data
      ?.filter(cls => {
        const classRank = getClassRank(cls.name);
        return classRank === currentClassRank || classRank === nextClassRank;
      })
      .sort((a, b) =>
        getClassRank(a.name) - getClassRank(b.name)
      ) || [];

  // Find the immediate next session by name (assuming sessions are sorted by name ascending)
  let nextSession: SessionData | undefined = undefined;
  if (currentSessionName && sessionsQuery.data) {
    const sortedSessions = [...sessionsQuery.data].sort((a, b) => a.name.localeCompare(b.name));
    const currentIdx = sortedSessions.findIndex(s => s.name === currentSessionName);
    if (currentIdx !== -1 && currentIdx < sortedSessions.length - 1) {
      nextSession = sortedSessions[currentIdx + 1];
    }
  }

  // Auto-set next session if available
  useEffect(() => {
    if (open && nextSession && !selectedSessionId) {
      setSelectedSessionId(nextSession.id ?? '');
    } else if (open && !nextSession) {
      setSelectedSessionId('');
    }
  }, [open, nextSession, selectedSessionId]);

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={
        isBulk
          ? (t('bulk_promote_students') || `Promote ${studentIds?.length || 0} Students`)
          : t('promote_student')
      }
      maxWidth="xs"
      fullWidth
      hideActions
      disableEscapeKeyDown
    >
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>{t('promotion_class')}</InputLabel>
          <Select
            value={selectedClassId}
            onChange={e => setSelectedClassId(Number(e.target.value))}
            label={t('promotion_class')}
          >
            {eligibleClasses.map(cls => (
              <MenuItem key={cls.id ?? 0} value={cls.id ?? 0}>{cls.name} {cls.section}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal" required disabled>
          <InputLabel>{t('promotion_session')}</InputLabel>
          <Select
            value={selectedSessionId}
            onChange={e => setSelectedSessionId(Number(e.target.value))}
            label={t('promotion_session')}
          >
            {nextSession ? (
              <MenuItem key={nextSession.id ?? 0} value={nextSession.id ?? 0}>{nextSession.name}</MenuItem>
            ) : (
              <MenuItem value="" disabled>{t('no_next_session_available')}</MenuItem>
            )}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>{t('school_type')}</InputLabel>
          <Select
            value={schoolType}
            label={t('school_type')}
            onChange={e => setSchoolType(e.target.value)}
          >
            <MenuItem value="SAME">{t('same')}</MenuItem>
            <MenuItem value="OTHER">{t('other')}</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={
              (promoteStudentMutation.isLoading || isBulkPromoting) ||
              !selectedClassId ||
              !selectedSessionId
            }
          >
            {(promoteStudentMutation.isLoading || isBulkPromoting) ? (
              <CircularProgress size={22} />
            ) : (
              showPromoteWithSubjects
                ? (t('promote_student_with_subjects') || 'Promote Student with Subjects')
                : (t('promote_students') || 'Promote Students')
            )}
          </Button>
        </Box>
      </form>
    </CommonModal>
  );
};

export default PramoteStudentFormModal;
